class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      if (!cartItems || typeof cartItems.updateQuantity !== 'function') return;
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }

          const deliveryTarget = document.getElementById('CartDrawer-Delivery');
          const deliverySource = html.getElementById('CartDrawer-Delivery');
          if (deliveryTarget && deliverySource) {
            deliveryTarget.innerHTML = deliverySource.innerHTML;
          }

          const countTarget = document.getElementById('CartDrawer-Count');
          const countSource = html.getElementById('CartDrawer-Count');
          if (countTarget && countSource) {
            countTarget.innerHTML = countSource.innerHTML;
          }

          const giftTarget = document.getElementById('CartDrawer-Gift');
          const giftSource = html.getElementById('CartDrawer-Gift');
          if (giftTarget && giftSource) {
            giftTarget.innerHTML = giftSource.innerHTML;
          }

          const drawerEl = document.querySelector('cart-drawer');
          if (drawerEl) {
            drawerEl.classList.toggle('is-empty', html.querySelector('cart-drawer')?.classList.contains('is-empty'));
          }

          if (window.IfCartDelivery) {
            window.IfCartDelivery.refresh();
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    const index = String(line);
    const requested = parseInt(quantity, 10);
    const removeEl =
      event && event.currentTarget && event.currentTarget.tagName === 'CART-REMOVE-BUTTON'
        ? event.currentTarget
        : null;
    const isRemove = requested === 0 || Boolean(removeEl);
    const inputEl =
      document.getElementById(`Quantity-${index}`) ||
      document.getElementById(`Drawer-quantity-${index}`) ||
      (event && event.target && event.target.closest ? event.target.closest('quantity-input') : null);
    const input = inputEl && inputEl.tagName === 'INPUT' ? inputEl : inputEl && inputEl.querySelector('input');
    const key =
      (removeEl && removeEl.dataset.key) ||
      (input && input.dataset.key) ||
      '';
    let qty = requested;
    if (!isRemove && input) {
      const liveQty = parseInt(input.value, 10);
      if (Number.isFinite(liveQty)) qty = liveQty;
    }
    if (!Number.isFinite(qty)) qty = 0;

    this._qtyQueued = { line: index, quantity: qty, event, name, variantId, key };
    if (this._qtyBusy) return;
    this._flushQuantityQueue();
  }

  _flushQuantityQueue() {
    const job = this._qtyQueued;
    if (!job) {
      this._qtyBusy = false;
      return;
    }
    this._qtyQueued = null;
    this._qtyBusy = true;
    this._sendQuantityUpdate(job);
  }

  _sectionElement(section) {
    const root = document.getElementById(section.id);
    if (!root) return document.querySelector(section.selector);
    if (section.selector && root.matches(section.selector)) return root;
    return (section.selector && root.querySelector(section.selector)) || root;
  }

  _sendQuantityUpdate(job) {
    this.enableLoading(job.line);

    const giftApi = window.IfFreeGift;
    const dropGifts =
      giftApi && job.key && typeof giftApi.giftsToRemoveForQty === 'function'
        ? giftApi.giftsToRemoveForQty(job.key, job.quantity)
        : [];
    if (dropGifts.length) {
      if (typeof giftApi.hideGiftLines === 'function') giftApi.hideGiftLines();
      const preview = typeof giftApi.previewAfterQty === 'function' ? giftApi.previewAfterQty(job.key, job.quantity) : null;
      if (preview) {
        if (window.IfCartDelivery) window.IfCartDelivery.update(preview);
        if (typeof giftApi.paintTiers === 'function') giftApi.paintTiers(preview);
      }
    }

    const sections = this.getSectionsToRender().map((section) => section.section);
    const eventTarget =
      job.quantity === 0 || (job.event && job.event.currentTarget && job.event.currentTarget.tagName === 'CART-REMOVE-BUTTON')
        ? 'clear'
        : 'change';
    let request;

    if (dropGifts.length && job.key) {
      const updates = {};
      updates[job.key] = job.quantity;
      dropGifts.forEach((gift) => {
        if (gift.key && gift.key !== job.key) updates[gift.key] = 0;
      });
      request = fetch(`${routes.cart_update_url}`, {
        ...fetchConfig(),
        credentials: 'same-origin',
        body: JSON.stringify({
          updates,
          sections,
          sections_url: window.location.pathname,
        }),
      });
    } else {
      const payload = {
        quantity: job.quantity,
        sections,
        sections_url: window.location.pathname,
      };
      if (job.key) payload.id = job.key;
      else payload.line = parseInt(job.line, 10);
      request = fetch(`${routes.cart_change_url}`, {
        ...fetchConfig(),
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      });
    }

    request
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        if (this._qtyQueued) return parsedState;

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${job.line}`) || document.getElementById(`Drawer-quantity-${job.line}`);

          if (parsedState.errors) {
            if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(job.line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const html = parsedState.sections && parsedState.sections[section.section];
            if (!html) return;
            const dest = this._sectionElement(section);
            if (!dest) return;
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const src = parsed.querySelector(section.selector) || parsed.getElementById(section.id);
            if (src) dest.innerHTML = src.innerHTML;
          });

          if (parsedState.item_count === 0 && cartDrawerWrapper) {
            const drawerHtml = parsedState.sections && parsedState.sections['cart-drawer'];
            if (drawerHtml && !cartDrawerWrapper.querySelector('.drawer__inner-empty')) {
              const emptyDoc = new DOMParser().parseFromString(drawerHtml, 'text/html');
              const emptySrc = emptyDoc.querySelector('.drawer__inner-empty');
              const inner = cartDrawerWrapper.querySelector('.drawer__inner');
              const header = inner && inner.querySelector('.drawer__header');
              if (emptySrc && header) header.insertAdjacentElement('afterend', emptySrc.cloneNode(true));
            }
            const countEl = document.getElementById('CartDrawer-Count');
            if (countEl) countEl.textContent = '';
          }

          if (window.IfCartDelivery && parsedState.total_price !== undefined) {
            window.IfCartDelivery.update(parsedState);
          }

          const matched = job.key
            ? (parsedState.items || []).find((item) => item.key === job.key)
            : parsedState.items && parsedState.items[parseInt(job.line, 10) - 1];
          const updatedValue = matched ? matched.quantity : undefined;
          const latestInput =
            document.getElementById(`Quantity-${job.line}`) || document.getElementById(`Drawer-quantity-${job.line}`);
          if (latestInput && updatedValue !== undefined) latestInput.value = String(updatedValue);

          let message = '';
          if (updatedValue !== undefined && updatedValue !== job.quantity) {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
          this.updateLiveRegions(job.line, message);

          const lineItem =
            document.getElementById(`CartItem-${job.line}`) || document.getElementById(`CartDrawer-Item-${job.line}`);
          if (lineItem && job.name && lineItem.querySelector(`[name="${job.name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${job.name}"]`))
              : lineItem.querySelector(`[name="${job.name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            const emptyEl = cartDrawerWrapper.querySelector('.drawer__inner-empty');
            const emptyLink = emptyEl && emptyEl.querySelector('a');
            if (emptyEl && emptyLink) trapFocus(emptyEl, emptyLink);
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, job.event);

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: parsedState,
          variantId: job.variantId,
        });
        if (window.IfFreeGift) {
          if (typeof window.IfFreeGift.rememberCart === 'function') window.IfFreeGift.rememberCart(parsedState);
          if (parsedState.items && !this._qtyQueued && !dropGifts.length) {
            window.IfFreeGift.sync(parsedState);
          } else if (parsedState.items && typeof window.IfFreeGift.paintTiers === 'function') {
            window.IfFreeGift.paintTiers(parsedState);
          }
        }
      })
      .catch(() => {
        document.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        if (dropGifts.length && window.IfFreeGift && typeof window.IfFreeGift.restoreGiftLines === 'function') {
          window.IfFreeGift.restoreGiftLines();
        }
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
        const input =
          document.getElementById(`Quantity-${job.line}`) || document.getElementById(`Drawer-quantity-${job.line}`);
        if (input) input.value = input.getAttribute('value');
      })
      .finally(() => {
        this._qtyBusy = false;
        if (this._qtyQueued) {
          this._flushQuantityQueue();
        } else {
          this.disableLoading(job.line);
        }
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.add('cart__items--disabled');

    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.classList.add('is-updating');
    const updating = document.getElementById('CartDrawer-Updating');
    if (updating) updating.hidden = false;

    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.remove('cart__items--disabled');

    document.querySelectorAll('.cart-item .loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));

    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.classList.remove('is-updating');
    const updating = document.getElementById('CartDrawer-Updating');
    if (updating) updating.hidden = true;
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
